import React, { useEffect, useRef } from 'react';

function loadRazorpayScript(src) {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const PayButton = ({ amount, student, paymentId, hidden }) => {
  const paymentParams = useRef({ amount, student, paymentId });

  useEffect(() => {
    paymentParams.current = { amount, student, paymentId };
  }, [amount, student, paymentId]);

  useEffect(() => {
    const handler = (e) => {
      console.log('Received startRazorpayPayment event', e.detail);
      const { amount, student, paymentId, paymentIds } = e.detail;
      handlePayment(amount, student, paymentId, paymentIds);
    };
    window.addEventListener('startRazorpayPayment', handler);
    return () => window.removeEventListener('startRazorpayPayment', handler);
  }, []);

  const handlePayment = async (overrideAmount, overrideStudent, overridePaymentId, overridePaymentIds) => {
    const amt = overrideAmount || paymentParams.current.amount;
    const stud = overrideStudent || paymentParams.current.student;
    const payId = overridePaymentId || paymentParams.current.paymentId;
    const payIds = overridePaymentIds || (payId ? [payId] : []);
    if (!payId) {
      alert('Payment record not created. Please try again.');
      return;
    }
    const res = await loadRazorpayScript('https://checkout.razorpay.com/v1/checkout.js');
    if (!res) {
      alert('Razorpay SDK failed to load.');
      return;
    }

    // 1. Get order_id from backend (pass all payment IDs for multi-month orders)
    const orderRes = await fetch('/api/payments/create-order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: amt, payment_id: payId, payment_ids: payIds })
    });
    const orderData = await orderRes.json();
    if (!orderData.id) {
      alert('Failed to create order.');
      return;
    }

    // 2. Setup Razorpay options
    // Note: When using order_id, Razorpay automatically uses the amount from the order
    // We don't need to pass amount separately - it's already set correctly in paise on the server
    // Use live key in production, test key in development
    const isProduction = process.env.NODE_ENV === 'production';
    const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY_ID || 
      (isProduction ? "rzp_live_SWwRw6goVVOfQD" : "rzp_test_SWwaoeGTp4HJmt");
    
    const options = {
      key: razorpayKey,
      currency: orderData.currency || "INR",
      name: "Chinmudra Academy",
      description: "Student Fee Payment",
      image: "https://chinmudraacademy.com/logo.png",
      order_id: orderData.id,
      handler: async function (response) {
        // Handle successful payment
        try {
          const verifyRes = await fetch('/api/payments/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              payment_id: payId
            })
          });
          const verifyData = await verifyRes.json();
          if (verifyData.success) {
            alert('Payment successful!');
            window.location.reload();
          } else {
            alert('Payment verification failed. Please contact support.');
          }
        } catch (err) {
          console.error('Payment verification error:', err);
          alert('Payment verification failed. Please contact support.');
        }
      },
      modal: {
        ondismiss: async function() {
          // User closed the payment dialog without completing payment
          // Mark the payment as failed
          try {
            const token = localStorage.getItem('access_token');
            await fetch(`/api/payments/${payId}/mark-failed`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                reason: 'Payment cancelled by user'
              })
            });
            console.log('Payment marked as failed - user cancelled');
          } catch (err) {
            console.error('Error marking payment as failed:', err);
          }
          // Reload to show updated status
          window.location.reload();
        }
      },
      prefill: {
        name: stud?.name || '',
        email: stud?.email || '',
        contact: stud?.phone || ''
      },
      notes: {
        address: "Chinmudra Academy"
      },
      theme: { color: "#D4AF37" }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  if (hidden) return null;

  return (
    <button id="rzp-button1" onClick={() => handlePayment()} style={{background: '#D4AF37', color: '#fff', padding: '12px 32px', border: 'none', borderRadius: 4, fontWeight: 600}}>
      Pay Now
    </button>
  );
};

export default PayButton;
